import { Avatar } from 'baseui/avatar';
import { capitalize, isNumber } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Box } from '../../../Components/Booking/Styles';
import { CampusTeamSelection } from '../../../Components/CampusTeam/CampusTeamSelection';
import { EditCampusTeamMember } from '../../../Components/CampusTeam/types';
import { FinalButton } from '../../../Components/FinalButton';
import { BirthdateInputRHF, InputRHF, PhoneInputRHF, SelectRHF } from '../../../Components/Form';
import { Icon, icons } from '../../../Components/Icons';
import { MantraSpinner } from '../../../Components/LoadingOverlay';
import { modalFactory } from '../../../Components/Modal';
import { Notification } from '../../../Components/Notification';
import { useCareTypeConfigContext } from '../../../Components/CareTypeConfigContext';
import { useCurrentProvider } from '../../../Components/Permissions';
import { useProviderNetworkContext } from '../../../Components/ProviderNetwork/ProviderNetworkContext';
import { useDisplayableType } from '../../../Components/ProviderNetwork/providerNetworkUtils';
import { AppointmentOption } from '../../../Components/ProviderNetwork/types';
import { When } from '../../../Components/When';
import { Text } from '../../../globalStyles';
import {
  SamlConfigurationType,
  StateCodes,
  useAdminCreateUserWithAppointmentMutation,
  useEnrollModalQuery,
  useSamlConfigurationQuery,
} from '../../../graphQL';
import { stateOptions } from '../../../states';
import { borderRadius, stripPhoneNumber } from '../../../utils';
import { FormFields, Label } from './Common';
import { parseCampusTeamToMemberInput } from '../../../Components/CampusTeam/campusTeamUtils';
import { getGraphqlFormatErrors } from '../../../utils/graphql';
import { DuplicateEmailMessage } from './DuplicateEmailMessage';
const required = 'This field is required.';

const Modal = modalFactory({
  style: { ...borderRadius('4px'), overflow: 'hidden', maxWidth: '60rem' },
});

export type BookingSuccess = {
  appointment: AppointmentOption;
  userId: number;
  name: string;
};

export type EnrollmentErrorModalProps = {
  userEmail: string;
  errorCode: string;
};

type ModalProps = {
  onClose: () => void;
  appointment: AppointmentOption;
  onComplete: (s: BookingSuccess) => void;
};

export const SponsoredCareModal = ({ onClose, appointment, onComplete }: ModalProps) => {
  const {
    searchVariables: { state },
  } = useProviderNetworkContext();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const getDisplayableType = useDisplayableType();
  const [showPreferredName, setShowPreferredName] = useState(false);
  const { currentProvider, appView } = useCurrentProvider();
  const organizationId = currentProvider.organizations[0].id;
  const [campusTeamError, setCampusTeamError] = useState<string>('');
  const [dobError, setDobError] = useState<boolean>(false);
  const [duplicateEmailError, setDuplicateEmailError] = useState<boolean>(false);

  const { data: samlConfigurationQueryData } = useSamlConfigurationQuery({
    variables: {
      organizationId,
      type: SamlConfigurationType.User,
    },
  });
  const form = useForm<FormFields>({
    defaultValues: { geoState: state ?? undefined },
    reValidateMode: 'onSubmit',
  });

  const { description, duration } = getApptDetails(appointment, getCareTypeDisplay);

  const { data, loading } = useEnrollModalQuery({
    variables: { organizationId, providerIds: [appointment.provider.id] },
  });

  const [campusTeam, setCampusTeam] = useState<EditCampusTeamMember[]>([]);

  const [createUser, mutation] = useAdminCreateUserWithAppointmentMutation({
    onCompleted: res => {
      const typeName = res.adminCreateUser.__typename;
      if (
        typeName === 'AdminCreateUserDuplicateEmailDetailedResponse' ||
        typeName === 'AdminCreateUserDuplicateEmailResponse'
      ) {
        setDuplicateEmailError(true);
        return;
      }

      if (typeName === 'AdminCreateUserError') {
        return;
      }

      const { id, name } = res.adminCreateUser;

      onComplete({ appointment, userId: id, name });
      return;
    },
    onError: error => {
      if (isMinorConsentError(error)) {
        setDobError(true);
      }
    },
  });

  const formatErrors = useMemo(() => getGraphqlFormatErrors(mutation.error), [mutation.error]);

  const childOrganizationId = form.watch('childOrganizationId');

  const { setValue } = form;
  useEffect(() => {}, [setValue, childOrganizationId]);

  const onSubmit = form.handleSubmit(values => {
    const campusTeamWithRelations = parseCampusTeamToMemberInput(campusTeam, setCampusTeamError);

    if (!campusTeamWithRelations) {
      return;
    }

    setCampusTeamError('');
    setDobError(false);
    setDuplicateEmailError(false);

    createUser({
      variables: {
        input: {
          organizationId: values.childOrganizationId ?? organizationId,
          firstName: values.firstName,
          lastName: values.lastName,
          preferredName: values.preferredName,
          email: values.email,
          phone: stripPhoneNumber(values.phone),
          birthDate: moment(values.birthDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
          campusTeam: campusTeamWithRelations,
          careTypes: [appointment.careType],
          geoState: values.geoState as StateCodes,
          samlUniqueId: values.uniqueId,
        },
        intakeAppointment: {
          startTime: new Date(appointment.startTime.format()),
          endTime: new Date(appointment.endTime.format()),
          providerId: appointment.provider.id,
          // if appointment was created using the dedicated group model allocations, the appointment.organizationId value will be null
          organizationId: appointment.organizationId,
          isFeeForServiceTime: appointment.isFeeForServiceTime,
          careType: appointment.careType,
        },
      },
    });
  });

  return (
    <Modal isOpen onClose={onClose}>
      <FormProvider {...form}>
        <div className="flex">
          <div className="flex-1" style={{ background: '#EFEFEF', padding: '55px 62px' }}>
            <Text.h2 className="mb4">Create student account to book appointment</Text.h2>
            {loading ? (
              <div className="flex justify-center pv3">
                <MantraSpinner size={25} simple />
              </div>
            ) : (
              <div>
                <Text.bodyBold>
                  {description} {`(${duration} min)`}
                </Text.bodyBold>
                <Text.body className="flex items-center">
                  <Icon icon="iconsBlackApptSvg" alt="Calendar" className="mr1" />
                  {appointment.startTime.format('M/D/YYYY h:mm a')} -{' '}
                  {appointment.endTime.clone().format('h:mma z')}
                </Text.body>
                {/* Provider Icon */}
                <div className="mt4 flex flex-row gap-3 items-center">
                  <Avatar
                    size="3rem"
                    overrides={{ Avatar: { style: { maxWidth: '2.5rem' } } }}
                    src={
                      data?.providersById.find(p => p.id === appointment.provider.id)?.portrait
                        ?.url ?? icons.assetsDefaultPicturePng
                    }
                    name={appointment.provider.name}
                  />
                  <div>
                    <Text.h3>
                      {appointment.provider.name.split(' ').map(capitalize).join(' ')}
                    </Text.h3>
                    <Text.bodySmall kind="grayText">
                      {getDisplayableType(appointment.provider)}
                    </Text.bodySmall>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex-1" style={{ padding: '55px 62px' }}>
            {mutation.error && !isMinorConsentError(mutation.error) && (
              <Notification kind="negative">
                {mutation.error.message ?? 'An unexpected error occurred.'}
              </Notification>
            )}

            {formatErrors.input && (
              <Notification kind="negative">{formatErrors.input.join(' ')}</Notification>
            )}

            {duplicateEmailError && <DuplicateEmailMessage appView={appView} />}

            <div className="flex">
              <Label name="firstName" label="First Name" className="mb0">
                <InputRHF
                  name="firstName"
                  rules={{ required }}
                  controlProps={{ className: 'mb2' }}
                />
              </Label>
              <Label name="lastName" label="Last Name" className="ml3 mb0">
                <InputRHF
                  name="lastName"
                  rules={{ required }}
                  controlProps={{ className: 'mb2' }}
                />
              </Label>
            </div>
            {showPreferredName ? (
              <Label name="preferredName" label="Preferred Name" className="mb0">
                <InputRHF name="preferredName" />
              </Label>
            ) : (
              <Text.linkButton className="b mb3" onClick={() => setShowPreferredName(true)}>
                + Add Preferred Name
              </Text.linkButton>
            )}
            <Label name="birthDate" label="Date of Birth">
              <BirthdateInputRHF name="birthDate" controlProps={{ required: true }} />

              {dobError && (
                <Text.bodySmallBold kind="dangerBgAlt">
                  Eligible minors under 18 must be referred via our{' '}
                  <Text.link to={`/organizations/${organizationId}/students/enroll`}>
                    standard referral form
                  </Text.link>
                  , as visits cannot be scheduled for minors until the consent process is completed.
                </Text.bodySmallBold>
              )}
            </Label>
            <Label name="email" label="Student University Email Address">
              <InputRHF name="email" rules={{ required }} />
            </Label>
            <Label name="geoState" label="Student Residential State">
              <SelectRHF
                name="geoState"
                options={stateOptions}
                rules={{
                  required,
                }}
              />
            </Label>
            <Label name="phone" label="Student Phone Number">
              <PhoneInputRHF name="phone" rules={{ required }} />
            </Label>

            {samlConfigurationQueryData?.organization.samlConfiguration
              ?.enableManualUniqueIdEdits && (
              <Label name="samlUniqueId" label="Unique Id" className="mb0">
                <InputRHF
                  name="samlUniqueId"
                  rules={{ required }}
                  controlProps={{ className: 'mb2' }}
                />
              </Label>
            )}

            {data && data.organization.children.length > 0 && (
              <Label name="childOrganizationId" label="Campus">
                <SelectRHF
                  name="childOrganizationId"
                  options={
                    data?.organization.children.map(org => ({ id: org.id, label: org.name })) ?? []
                  }
                  rules={{ required }}
                />
              </Label>
            )}
            <When isTruthy={isNumber(organizationId)}>
              <CampusTeamSelection
                campusTeam={campusTeam ?? []}
                setCampusTeam={setCampusTeam}
                organizationId={organizationId}
                relationshipError={campusTeamError}
              />
            </When>
            <FinalButton
              kind="primary"
              className="w-100"
              type="submit"
              onClick={onSubmit}
              loading={mutation.loading}
            >
              Submit
            </FinalButton>
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
};

type EnrollmentSuccessProps = BookingSuccess;

export const EnrollmentSuccess = ({ appointment, userId, name }: EnrollmentSuccessProps) => {
  const history = useHistory();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const { duration, description } = getApptDetails(appointment, getCareTypeDisplay);
  return (
    <SuccessWrapper>
      <Icon icon="iconsCircleCheckSvg" alt="Success" className="center db" size={26} />
      <div className="mh2">
        <Text.h2>Appointment booked!</Text.h2>
        <Text.body className="mb4">
          Student will receive a confirmation email with appointment details.
        </Text.body>
        <Text.body>
          This appointment will take place in the student’s Mantra Health account. Please advise
          student to complete onboarding before their appointment, including reviewing &amp;
          agreeing to all releases.
        </Text.body>
        <Box className="mv4">
          <Text.body>{name}</Text.body>
          <Text.body>
            {description} {`(${duration} min) with ${appointment.provider.name}`}
          </Text.body>
          <Text.body className="flex items-center">
            <Icon icon="iconsBlackApptSvg" alt="Calendar" className="mr1" />
            {appointment.startTime.format('M/D/YYYY h:mm a')} -{' '}
            {appointment.endTime.clone().format('h:mma z')}
          </Text.body>
        </Box>
        <FinalButton
          kind="outline_black"
          className="w-100 mb3"
          onClick={() => history.push(`/users/${userId}`)}
        >
          Go to patient profile
        </FinalButton>
      </div>
    </SuccessWrapper>
  );
};

export const SuccessWrapper = styled.div`
  max-width: 400px;
  margin: 4rem auto;
`;

const getApptDetails = (
  appointment: AppointmentOption,
  getCareTypeDisplay: (careType: AppointmentOption['careType']) => {
    appointmentIntakeLabel: string;
  }
) => {
  const duration = moment(appointment.endTime).diff(appointment.startTime, 'minutes');
  const description = getCareTypeDisplay(appointment.careType).appointmentIntakeLabel;
  return { duration, description };
};

const isMinorConsentError = (error: Error) => error.message === 'Minor consent needed';
